import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Checkbox from 'apollo-react/components/Checkbox';
import Grid from 'apollo-react/components/Grid';
import MenuButton from 'apollo-react/components/MenuButton';
import Modal from 'apollo-react/components/Modal';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UploadCustomerLogo from './UploadCustomerLogo';
import { deleteCustomLogo } from '../../../actions/CustomerLogoActions';
import {
  changeDashboardEditingState,
  deleteDashboard,
  fetchDashboardConfigByKey,
  handleRequestCancel,
  saveDashboardConfig,
} from '../../../actions/DashboardConfigActions';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getIsRtlLanguage } from '../../../selectors/AuthSelectors';
import {
  getDashboardConfig,
  getIsDashboardPersonalized,
  getShowDashboardSwitcher,
} from '../../../selectors/DashboardSelectors';
import { getUITA } from '../../../utils/commonUtils';
import { DEFAULT_DASHBOARD_KEY, GLOBAL_DASHBOARD_KEY } from '../constants';

const useStyles = makeStyles({
  root: {
    marginTop: '44px',
    height: '48px',
    fontTamily: 'Proxima Nova',
    fontSize: '32px',
    fontWeight: 600,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: '#000000',
    marginLeft: '20px',
    backgroundColor: 'red',
  },
  dashboardStyled: {
    fontSize: '32px',
    fontWeight: 'bold',
    paddingLeft: '20px',
    paddingBottom: '10px',
    justifyContent: 'space-between',
  },
  dashboardTile: {
    paddingLeft: '12px',
  },
  buttonSpacer: {
    marginRight: '6px',
    '& button': {
      marginRight: '5px',
    },
  },
  dashboardStyledRtl: {
    fontSize: '32px',
    fontWeight: 'bold',
    paddingBottom: '10px',
    justifyContent: 'space-between',
  },
  dashboardTileRtl: {
    paddingLeft: '8px',
  },
  title: {
    marginRight: 10,
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
  },
});

const DashboardHeader = ({ mode }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();

  const showDashboardSwitcher = useSelector(getShowDashboardSwitcher);
  const isDashboardPersonalized = useSelector(getIsDashboardPersonalized);
  const { isExternalUser, actorCode } = useSelector((state) => state.auth);
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const {
    code: dashboardCode,
    hasAdminRights,
    userSpecificDashboard,
    globalDefaultDashboard,
    isEditGlobal,
  } = useSelector(getDashboardConfig);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteLogoModalOpen, setIsDeleteLogoModalOpen] = useState(false);

  const handleActionRequest = useCallback(
    async (action, isEditGlobal) => {
      const shouldFetchDefaultDashboard =
        isEditGlobal && dashboardCode !== GLOBAL_DASHBOARD_KEY && !globalDefaultDashboard;

      if (dashboardCode !== DEFAULT_DASHBOARD_KEY && shouldFetchDefaultDashboard) {
        await dispatch(fetchDashboardConfigByKey(DEFAULT_DASHBOARD_KEY));
      }

      dispatch(changeDashboardEditingState(action, isEditGlobal));
    },
    [dashboardCode, dispatch, globalDefaultDashboard]
  );

  function handleSaveDashboard(params) {
    dispatch(saveDashboardConfig(params));
  }

  const handleCurrentDashboardChange = (e, checked) => {
    dispatch(fetchDashboardConfigByKey(checked ? GLOBAL_DASHBOARD_KEY : actorCode));
  };

  const handleModalShow = useCallback(() => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  }, [isDeleteModalOpen]);

  const handleDelete = (dashboardCode) => {
    dispatch(deleteDashboard(dashboardCode));
    handleModalShow();
  };

  const handleModalShowForUpload = useCallback(() => {
    setIsUploadModalOpen(!isUploadModalOpen);
  }, [isUploadModalOpen]);

  const showCustomAction = !userSpecificDashboard || dashboardCode === actorCode;
  const showGlobalAction =
    hasAdminRights && (!globalDefaultDashboard || dashboardCode === GLOBAL_DASHBOARD_KEY);

  const { isMobileDevice } = useWindowDimensions();

  const actionOptions = useMemo(
    () =>
      [
        {
          text: translateString('Edit Dashboard'),
          onClick: () => handleActionRequest('edit', false),
          visible: showCustomAction,
        },
        {
          text: translateString('Edit Global Dashboard'),
          onClick: () => handleActionRequest('edit', true),
          visible: showGlobalAction,
        },
        {
          text: translateString('Dashboard Parts'),
          onClick: () => handleActionRequest('dashboardParts', false),
          visible: showCustomAction,
        },
        {
          text: translateString('Global Dashboard Parts'),
          onClick: () => handleActionRequest('dashboardParts', true),
          visible: showGlobalAction,
        },
        {
          text: translateString('Delete Personalization'),
          onClick: () => handleModalShow(),
          visible: isDashboardPersonalized,
        },
        {
          text: translateString('Delete Global Dashboard'),
          onClick: () => handleModalShow(),
          visible: hasAdminRights && dashboardCode === GLOBAL_DASHBOARD_KEY,
        },
        {
          text: translateString('Upload Brand Logo'),
          onClick: () => handleModalShowForUpload(),
          visible: showGlobalAction,
        },
        {
          text: translateString('Remove Brand Logo'),
          onClick: () => setIsDeleteLogoModalOpen(true),
          visible: showGlobalAction,
        },
      ]
        .filter((opt) => opt.visible)
        .map((x) => ({ text: x.text, onClick: x.onClick })),
    [
      dashboardCode,
      handleActionRequest,
      handleModalShow,
      handleModalShowForUpload,
      hasAdminRights,
      isDashboardPersonalized,
      showCustomAction,
      showGlobalAction,
      translateString,
    ]
  );

  function renderConfigButton() {
    switch (mode) {
      case 'edit':
        return (
          <div className={classes.buttonSpacer}>
            <Button
              aria-haspopup="true"
              color="primary"
              variant="primary"
              onClick={() => handleSaveDashboard({ isEditGlobal })}
            >
              {translateString('Save Dashboard')}
            </Button>
            <Button
              color="primary"
              onClick={() =>
                dispatch(
                  handleRequestCancel({
                    action: 'general',
                    userSpecificDashboard,
                    dashboardCode,
                    actorCode,
                    isEditGlobal,
                    hasAdminRights,
                  })
                )
              }
            >
              {translateString('Cancel')}
            </Button>
          </div>
        );

      default:
        return (
          <>
            {!isMobileDevice && (
              <MenuButton
                maxItems={10}
                buttonText={translateString('Configure Dashboard')}
                menuItems={actionOptions}
                size="medium"
              />
            )}
          </>
        );
    }
  }

  return (
    <Grid
      container
      className={isRtlLanguage ? classes.dashboardStyledRtl : classes.dashboardStyled}
    >
      <Grid item>
        <div className={isRtlLanguage ? classes.dashboardTileRtl : classes.dashboardTile} />
      </Grid>
      <Grid item className={classes.actionContainer}>
        {hasAdminRights && mode === 'general' && showDashboardSwitcher && (
          <Checkbox
            label={translateString('Use Global Dashboard')}
            checked={dashboardCode === GLOBAL_DASHBOARD_KEY}
            onChange={handleCurrentDashboardChange}
          />
        )}
        {!isExternalUser && (
          <div className={isRtlLanguage ? classes.dashboardTileRtl : classes.dashboardTile}>
            {renderConfigButton()}
          </div>
        )}
      </Grid>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => handleModalShow()}
        variant="warning"
        title={translateString('Warning')}
        message={translateString('Confirm Delete Operation?')}
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{ 'ui-ta': getUITA('ConfirmPopupElement') }}
        buttonProps={[
          {
            variant: 'secondary',
            label: translateString('Cancel'),
            'ui-ta': getUITA('PopupButtonCANCEL'),
          },
          {
            variant: 'primary',
            label: translateString('Delete'),
            onClick: () => handleDelete(dashboardCode),
            'ui-ta': getUITA('PopupButtonOK'),
          },
        ]}
      />
      {isUploadModalOpen && (
        <UploadCustomerLogo
          isUploadModalOpen={isUploadModalOpen}
          handleModalShowForUpload={handleModalShowForUpload}
        />
      )}
      <DeleteConfirmationModal
        message={translateString('Are you sure you want to delete Brand Logo?')}
        open={isDeleteLogoModalOpen}
        onClose={() => setIsDeleteLogoModalOpen(false)}
        onDeleteConfirm={() => dispatch(deleteCustomLogo)}
      />
    </Grid>
  );
};

export default DashboardHeader;
